"use client";

import { useCallback, useEffect, useState } from "react";

const PUSH_OPEN_RETAIN_MODAL_DELAY_MS = 2000;
const PUSH_OPEN_RETAIN_MODAL_MAX_WAIT_MS = 5000;

type UsePushRetainModalParams = {
  /**
   * retain modal 所依赖的用户/会员上下文是否已经 ready。
   * 典型依赖包括：
   * - ownerInfo.id
   * - memberInfo
   */
  isRetainContextReady: boolean;
  /**
   * 当前是否满足续费挽留弹窗的业务条件。
   * 如果数据 ready 后该值仍然为 false，说明这次 push 不应该再触发 retain modal。
   */
  shouldShowRenew: boolean;
  /**
   * passive retain modal 当前是否不可用。
   *
   * 返回 true 的典型场景：
   * - 本会话已经展示过一次 auto / push retain modal
   * - 另一个被动展示流程正在打开弹窗
   */
  isPassiveRetainModalBlocked: () => boolean;
  /**
   * 真正执行“被动展示”retain modal 的方法。
   * auto 与 push 共用这一个入口，以保证两者走同一套 session 锁。
   */
  openPassiveRetainModal: () => Promise<boolean>;
};

/**
 * 专门处理“点击 retention push 后如何打开 retain modal”的逻辑。
 *
 * 之所以单独拆文件，是因为这套逻辑具有明显的 push 语义：
 * 1. push 到达时不一定已经拿到 ownerInfo / memberInfo
 * 2. 因此不能当场直接弹，只能先挂起等待
 * 3. 最多只等待 5s，避免用户进入页面很久后才突然补弹
 *
 * 后续如果产品要整体下掉 push 触发 retain modal，只需要：
 * - 删除 useInitApp 中的调用
 * - 移除本文件
 * 即可，尽量不污染手动/自动弹窗主逻辑。
 */
export const usePushRetainModal = ({
  isRetainContextReady,
  shouldShowRenew,
  isPassiveRetainModalBlocked,
  openPassiveRetainModal,
}: UsePushRetainModalParams) => {
  const [pendingPushRequestedAt, setPendingPushRequestedAt] = useState<
    number | null
  >(null);
  const [hasPushDelayElapsed, setHasPushDelayElapsed] = useState(false);

  /**
   * push 点击入口不会直接弹窗，而是先登记一个“待处理请求”。
   *
   * 原因：
   * - push 点击时机通常早于 ownerInfo / memberInfo hydration
   * - 当场直接尝试很容易因为数据未 ready 而失败
   * - 这里先记下请求，再由 effect 在依赖就绪后消费
   */
  const requestOpenRetainModalFromPush = useCallback(() => {
    setPendingPushRequestedAt(Date.now());
    setHasPushDelayElapsed(false);
  }, []);

  /**
   * 参考低信用弹窗的处理方式：
   * push 在真正尝试打开 retain modal 前，至少等待 2000ms。
   *
   * 原因不是等业务数据，而是等 `EdgenModalCreateAble.Root` 这类
   * lazyHydrate + requestIdleCallback 的全局 modal 宿主有机会完成挂载。
   *
   * 注意：
   * - 这里只是“最短等待时间”
   * - 不是说 2000ms 一到就立即弹，还要继续满足 ready / shouldShowRenew / session 锁等条件
   */
  useEffect(() => {
    if (pendingPushRequestedAt === null) {
      return;
    }

    const remainMs =
      pendingPushRequestedAt + PUSH_OPEN_RETAIN_MODAL_DELAY_MS - Date.now();
    if (remainMs <= 0) {
      setHasPushDelayElapsed(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setHasPushDelayElapsed(true);
    }, remainMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [pendingPushRequestedAt]);

  /**
   * push 最多只等待 5s。
   *
   * 这样既给首屏 hydration 一个合理缓冲时间，
   * 又避免用户进入页面很久之后才突然补弹 retain modal。
   */
  useEffect(() => {
    if (pendingPushRequestedAt === null) {
      return;
    }

    const deadline =
      pendingPushRequestedAt + PUSH_OPEN_RETAIN_MODAL_MAX_WAIT_MS;
    const remainMs = deadline - Date.now();
    if (remainMs <= 0) {
      setPendingPushRequestedAt(null);
      setHasPushDelayElapsed(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setPendingPushRequestedAt((currentValue) => {
        if (currentValue !== pendingPushRequestedAt) {
          return currentValue;
        }
        setHasPushDelayElapsed(false);
        return null;
      });
    }, remainMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [pendingPushRequestedAt]);

  /**
   * 消费 push 的待处理请求。
   *
   * 规则：
   * 1. 先至少等 2000ms，让 modal root 有机会完成 lazyHydrate
   * 2. 只要还在 5s 窗口内，就持续等 ownerInfo / memberInfo ready
   * 3. ready 后如果业务条件不满足（shouldShowRenew=false），直接放弃
   * 4. ready 且满足条件时，尝试走“被动展示”入口
   * 5. auto / push 共用同一把会话锁，因此同一次会话里最多展示一次
   */
  useEffect(() => {
    if (pendingPushRequestedAt === null) {
      return;
    }

    if (isPassiveRetainModalBlocked()) {
      setPendingPushRequestedAt(null);
      return;
    }

    if (!hasPushDelayElapsed) {
      return;
    }

    if (!isRetainContextReady) {
      return;
    }

    if (!shouldShowRenew) {
      setPendingPushRequestedAt(null);
      setHasPushDelayElapsed(false);
      return;
    }

    setPendingPushRequestedAt(null);
    setHasPushDelayElapsed(false);
    void openPassiveRetainModal();
  }, [
    hasPushDelayElapsed,
    isPassiveRetainModalBlocked,
    isRetainContextReady,
    openPassiveRetainModal,
    pendingPushRequestedAt,
    shouldShowRenew,
  ]);

  return {
    hasPendingPushRetainRequest: pendingPushRequestedAt !== null,
    requestOpenRetainModalFromPush,
  };
};
